import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { easeOut, fade } from "../../lib/motion";
import { cn } from "../../lib/utils";

/** Появление без отскока: панель чуть поднимается и проявляется, фон затемняется */
const enter = { type: "spring", stiffness: 420, damping: 38, mass: 0.9 } as const;
/** Смена размера (например, шаги постановки) — ровно, без отскока */
const resize = { duration: 0.38, ease: easeOut } as const;

/** Открытые окна по порядку — Esc закрывает только верхнее. */
const stack: symbol[] = [];

export function Modal({
  open,
  onClose,
  width,
  height,
  className,
  children,
  zIndex = 50,
}: {
  open: boolean;
  onClose: () => void;
  width: number;
  /** фиксированная высота; без неё — по содержимому. Меняется плавно вместе с шириной */
  height?: number;
  className?: string;
  children: ReactNode;
  zIndex?: number;
}) {
  // onClose через ref: иначе новая функция на каждом рендере переставляла бы окно наверх стопки.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const me = Symbol("modal");
    stack.push(me);
    const onKey = (e: KeyboardEvent) => {
      // Окно поверх окна («Ролик не выйдет?» над публикацией): Esc — только верхнему.
      if (stack[stack.length - 1] !== me) return;
      // Esc в открытом списке (дата, время, аккаунты) или в окне подтверждения
      // закрывает только их.
      if (e.key === "Escape" && !document.querySelector("[data-slot=popover-content], [role=alertdialog]")) closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      stack.splice(stack.indexOf(me), 1);
    };
  }, [open]);
  // Высоту анимируем, только если её хоть раз задали — иначе модалка просто по содержимому
  const sized = useRef(false);
  if (height !== undefined) sized.current = true;
  if (!open) sized.current = false;
  const size = sized.current ? { width, height: height ?? "auto" } : { width };

  return createPortal(
    <AnimatePresence>
      {open && (
        // montage-root: портал рисуется в body, вне страницы раздела, — палитру прототипа несём с собой
        <div className="montage-root fixed inset-0 flex items-center justify-center p-6 max-md:p-0" style={{ zIndex }}>
          <motion.div
            className="absolute inset-0 bg-overlay backdrop-blur-[3px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.18, ease: easeOut } }}
            transition={fade}
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            className={cn("relative flex max-h-full flex-col overflow-hidden border bg-background shadow-mlg max-md:h-dvh! max-md:max-h-dvh max-md:w-screen! max-md:rounded-none! max-md:border-0", className)}
            style={{ maxWidth: "100%", borderRadius: 32, transformOrigin: "50% 45%" }}
            initial={{ opacity: 0, scale: 0.97, y: 14, ...size }}
            animate={{ opacity: 1, scale: 1, y: 0, ...size, transition: { ...enter, width: resize, height: resize, opacity: { duration: 0.18, ease: easeOut } } }}
            exit={{ opacity: 0, scale: 0.98, y: 8, transition: { duration: 0.16, ease: [0.4, 0, 1, 1] } }}
          >
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
